import { captureMessage } from '@sentry/react';
import { not } from 'ramda';
import { UcumLhcUtils } from '@lhncbc/ucum-lhc';
import FEATURES from 'features';
import { isValidDate } from 'utils';
import { getRoundedValue } from './utils';

const DEFAULT_UNIT_NAME = 1;

export const ucumUtils = UcumLhcUtils.getInstance();

function preValidateUnit(unit) {
  /*
   Ideally, we would use a try...catch block to handle errors
   thrown by ucumUtils.validateUnitString. However, the library only logs the errors and does not re-throw them.
   Please see https://github.com/LHNCBC/ucum-lhc/blob/master/source/ucumLhcUtils.js#L637
  **/
  const isString = typeof unit === 'string';
  const noWhiteSpaces = unit?.indexOf?.(' ') === -1;
  return isString && noWhiteSpaces;
}

export function validUcumUnitSuggestions(unit) {
  const result =
    preValidateUnit(unit) && ucumUtils.validateUnitString(unit, true);

  if (!result || result.status === 'error') return [];

  const suggestionsForInvalidUnit = result?.suggestions?.[0]?.units;
  const matchedUnit = result?.unit ? [Object.values(result?.unit)] : null;
  const suggestions = matchedUnit ?? suggestionsForInvalidUnit;

  if (!suggestions?.length) return [];

  return suggestions;
}

export function validateUcumUnit(unit) {
  const result = preValidateUnit(unit) && ucumUtils.validateUnitString(unit);

  if (Boolean(unit?.length) && (!result || result.status === 'error')) {
    captureMessage(`Invalid unit received: ${unit}`);
  }

  return {
    isValidUnit: Boolean(result.ucumCode),
    validationResult: result,
  };
}

export function ucumConvertValues({
  fromUnit,
  fromValue,
  toUnit,
  molecularWeight,
  charge = 1,
}) {
  const conversionResult = ucumUtils.convertUnitTo(
    fromUnit,
    fromValue,
    toUnit,
    { molecularWeight, charge }
  );

  const unitName = conversionResult.toUnit?.name_;

  const containDefaultValueOne =
    unitName?.startsWith?.(DEFAULT_UNIT_NAME) ||
    unitName?.endsWith?.(DEFAULT_UNIT_NAME) ||
    unitName === DEFAULT_UNIT_NAME;

  const nameShown = containDefaultValueOne ? fromUnit : unitName;

  return {
    convertedValue: getRoundedValue(conversionResult.toVal),
    convertedUnitSymbol: containDefaultValueOne
      ? nameShown
      : conversionResult.toUnit?.printSymbol_,
    conversionResult,
  };
}

export function validateAndConvertToSelf({
  unit,
  value,
  molecularWeight,
  charge,
  fallbackUnit,
}) {
  let validationResult;

  try {
    validationResult = validateUcumUnit(unit);
  } catch {
    return { unit, value };
  }

  if (!validationResult?.isValidUnit) return { unit, value };

  const { convertedUnitSymbol, conversionResult } = ucumConvertValues({
    fromUnit: unit,
    fromValue: value,
    toUnit: unit,
    molecularWeight: molecularWeight,
    charge,
  });

  if (conversionResult.toUnit?.name_ === DEFAULT_UNIT_NAME) {
    return {
      unit,
      value,
    };
  }

  return {
    unit:
      convertedUnitSymbol ||
      fallbackUnit ||
      unit ||
      conversionResult.toUnit?.name_,
    value,
  };
}

export function findFirstValidUnit(data = []) {
  const firstValidUnit = data.find((x) => {
    const { isValidUnit } = validateUcumUnit(x?.unit);
    if (isValidUnit) return x;
    return false;
  });

  return { baseUnit: firstValidUnit?.unit };
}

function convertDateToTimestamp(entry) {
  const d = new Date(entry.effectiveTs);
  if (!isValidDate(d)) return null;
  return d.getTime();
}

export function convertChartData(data = []) {
  return data
    .map((x) => {
      const effectiveTs = convertDateToTimestamp(x);

      const { conversionResult } = ucumConvertValues({
        fromUnit: x?.unit,
        fromValue: x?.value,
        toUnit: data?.[0]?.unit,
        molecularWeight: x?.storyblock?.molecularWeight,
        charge: x?.storyblock?.charge,
      });

      const isDefaultUnit =
        conversionResult.toUnit?.name_ === DEFAULT_UNIT_NAME;

      const isConversionSuccess = conversionResult.status === 'succeeded';
      if (
        FEATURES.PHR.EXCLUDE_INVALID_UNITS_DASHBOARD &&
        not(
          effectiveTs &&
            Boolean(x?.unit) &&
            validateUcumUnit(x?.unit).isValidUnit
        )
      ) {
        return null;
      }
      //TODO EFFECTIVE DATE CONVERTION TO TIMESTAMP MAYBE NOT NEEDED
      return {
        ...x,
        effectiveTs,
        value: isConversionSuccess ? conversionResult?.toVal : x.value,
        unit:
          isDefaultUnit || !isConversionSuccess
            ? x?.unit
            : conversionResult?.toUnit?.csCode_,
      };
    })
    .filter(Boolean);
}
